/**
 * PositionFormModal - Модальное окно создания/редактирования должности партнёра
 */
import React, { useState, useEffect } from 'react';
import {
  Modal,
  TextInput,
  NumberInput,
  Button,
  Group,
  Stack,
  Switch,
  Alert,
} from '@mantine/core';
import { IconCheck, IconBriefcase, IconAlertCircle } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

const PositionFormModal = ({ opened, onClose, onSuccess, position }) => {
  const isEdit = !!position;

  const [formData, setFormData] = useState({
    name: '',
    sort_order: 0,
    is_active: true,
  });
  const [loading, setLoading] = useState(false);

  // Заполнение формы при редактировании
  useEffect(() => {
    if (position) {
      setFormData({
        name: position.name || '',
        sort_order: position.sort_order ?? 0,
        is_active: position.is_active ?? true,
      });
    } else {
      setFormData({
        name: '',
        sort_order: 0,
        is_active: true,
      });
    }
  }, [position, opened]);

  const handleSubmit = async () => {
    // Валидация
    if (!formData.name.trim()) {
      notifications.show({
        title: 'Ошибка',
        message: 'Введите название должности',
        color: 'red',
      });
      return;
    }

    setLoading(true);
    try {
      const url = isEdit ? `/api/partner-positions/${position.id}` : '/api/partner-positions';
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Ошибка сохранения');
      }

      notifications.show({
        title: 'Успешно',
        message: isEdit ? 'Должность обновлена' : 'Должность создана',
        color: 'green',
        icon: <IconCheck size={16} />,
      });

      onSuccess();
    } catch (err) {
      console.error('Failed to save position:', err);
      notifications.show({
        title: 'Ошибка',
        message: err.message,
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? 'Редактирование должности' : 'Новая должность'}
      centered
      radius="lg"
      size="md"
    >
      <Stack gap="md">
        {/* Предупреждение для системных должностей */}
        {isEdit && position?.is_system && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Системная должность"
            color="violet"
            variant="light"
          >
            Эта должность является системной и не может быть удалена.
            Вы можете изменить только порядок сортировки и статус.
          </Alert>
        )}

        <TextInput
          label="Название"
          placeholder="Например: Генеральный директор"
          required
          leftSection={<IconBriefcase size={16} />}
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          disabled={isEdit && position?.is_system}
        />

        <NumberInput
          label="Порядок сортировки"
          description="Меньшее значение = выше в списке"
          radius="xl"
          min={0}
          max={999}
          value={formData.sort_order}
          onChange={(value) => setFormData({ ...formData, sort_order: value || 0 })}
        />

        {isEdit && (
          <Switch
            label="Активна"
            description="Неактивные должности не отображаются при выборе"
            checked={formData.is_active}
            onChange={(e) => setFormData({ ...formData, is_active: e.currentTarget.checked })}
          />
        )}

        <Group justify="flex-end" gap="sm" mt="md">
          <Button variant="subtle" color="gray" onClick={onClose} disabled={loading}>
            Отмена
          </Button>
          <Button
            onClick={handleSubmit}
            loading={loading}
            leftSection={<IconCheck size={16} />}
          >
            {isEdit ? 'Сохранить' : 'Создать'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default PositionFormModal;
